'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    isLoading?: boolean;
}

export default function Pagination({
    currentPage,
    totalPages,
    onPageChange,
    isLoading = false
}: PaginationProps) {
    if (totalPages <= 1) return null;

    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                for (let i = 1; i <= 4; i++) {
                    pages.push(i);
                }
                pages.push('...');
                pages.push(totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1);
                pages.push('...');
                for (let i = totalPages - 3; i <= totalPages; i++) {
                    pages.push(i);
                }
            } else {
                pages.push(1);
                pages.push('...');
                pages.push(currentPage - 1);
                pages.push(currentPage);
                pages.push(currentPage + 1);
                pages.push('...');
                pages.push(totalPages);
            }
        }

        return pages;
    };

    const pageNumbers = getPageNumbers();

    return (
        <div data-component-id="src\components\blog\Pagination.tsx:58:8" data-component-path="src\components\blog\Pagination.tsx" data-component-line="58" data-component-end-line="111" data-component-file="Pagination.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20gap-2%20mt-12%22%7D" className="flex items-center justify-center gap-2 mt-12">
            {/* Previous Button */}
            <button data-component-id="src\components\blog\Pagination.tsx:60:12" data-component-path="src\components\blog\Pagination.tsx" data-component-line="60" data-component-end-line="67" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20w-10%20h-10%20rounded-lg%20border%20border-gray-300%20bg-white%20hover%3Abg-gray-50%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20transition-colors%20duration-200%22%7D"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1 || isLoading}
                className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                aria-label="Previous page"
            >
                <ChevronLeft data-component-id="src\components\blog\Pagination.tsx:66:16" data-component-path="src\components\blog\Pagination.tsx" data-component-line="66" data-component-end-line="66" data-component-file="Pagination.tsx" data-component-name="ChevronLeft" data-component-content="%7B%22elementName%22%3A%22ChevronLeft%22%2C%22className%22%3A%22text-gray-600%22%7D" size={20} className="text-gray-600" />
            </button>

            {/* Page Numbers */}
            <div data-component-id="src\components\blog\Pagination.tsx:70:12" data-component-path="src\components\blog\Pagination.tsx" data-component-line="70" data-component-end-line="100" data-component-file="Pagination.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-2%22%7D" className="flex items-center gap-2">
                {pageNumbers.map((page, index) => {
                    if (page === '...') {
                        return (
                            <span data-component-id="src\components\blog\Pagination.tsx:74:28" data-component-path="src\components\blog\Pagination.tsx" data-component-line="74" data-component-end-line="79" data-component-file="Pagination.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20w-10%20h-10%20text-gray-500%22%2C%22textContent%22%3A%22...%22%7D"
                                key={`ellipsis-${index}`}
                                className="flex items-center justify-center w-10 h-10 text-gray-500"
                            >
                                ...
                            </span>
                        );
                    }

                    const pageNum = page as number;
                    const isActive = pageNum === currentPage;

                    return (
                        <button data-component-id="src\components\blog\Pagination.tsx:87:24" data-component-path="src\components\blog\Pagination.tsx" data-component-line="87" data-component-end-line="97" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                            key={pageNum}
                            onClick={() => onPageChange(pageNum)}
                            disabled={isLoading}
                            className={`flex items-center justify-center w-10 h-10 rounded-lg font-medium transition-all duration-200 ${isActive
                                    ? 'bg-primary-accent text-white shadow-md scale-105'
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                            {pageNum}
                        </button>
                    );
                })}
            </div>

            {/* Next Button */}
            <button data-component-id="src\components\blog\Pagination.tsx:103:12" data-component-path="src\components\blog\Pagination.tsx" data-component-line="103" data-component-end-line="110" data-component-file="Pagination.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20justify-center%20w-10%20h-10%20rounded-lg%20border%20border-gray-300%20bg-white%20hover%3Abg-gray-50%20disabled%3Aopacity-50%20disabled%3Acursor-not-allowed%20transition-colors%20duration-200%22%7D"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages || isLoading}
                className="flex items-center justify-center w-10 h-10 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                aria-label="Next page"
            >
                <ChevronRight data-component-id="src\components\blog\Pagination.tsx:109:16" data-component-path="src\components\blog\Pagination.tsx" data-component-line="109" data-component-end-line="109" data-component-file="Pagination.tsx" data-component-name="ChevronRight" data-component-content="%7B%22elementName%22%3A%22ChevronRight%22%2C%22className%22%3A%22text-gray-600%22%7D" size={20} className="text-gray-600" />
            </button>
        </div>
    );
}
